import React, { useState, useContext, useEffect } from 'react';
import useAnalytics from 'helpers/useAnalytics';
import makeStyles from '@material-ui/core/styles/makeStyles';
import SourceContext from 'helpers/SourceContext';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { useData, useStatus, isNormal, combineStatuses, NORMAL, LOADING } from 'resift';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { useSnackbar } from 'notistack';
import classNames from 'classnames';
import makeGetSource from 'fetches/makeGetSource';
import makeGetSourceFileValidationResults from 'fetches/makeGetSourceFileValidationResults';
import makeUpdateSourceFile from 'fetches/makeUpdateSourceFile';
import useSource from 'pages/Directory/helpers/useSource';
import makeSourceCreateBaseUrl from 'pages/Directory/helpers/makeSourceCreateBaseUrl';
import { saveSource } from 'store/sources';
import uploadCsvFile from 'helpers/dataSync/uploadCsvFile';
import checkCsvUploadFileType from 'helpers/dataSync/checkCsvUploadFileType';

// Components
import CardHeader from '@material-ui/core/CardHeader';
import Link from '@material-ui/core/Link';

import FileDrop from 'components/FileDrop';
import Base from 'pages/Directory/Base';
import FileUploadInfoCard from './FileUploadInfoCard';
import CsvAnalysisDialog from './CsvAnalysisDialog';

import sourceData from 'pages/Directory/sourceData';
import { MAP_ATTRIBUTES_URL } from 'pages/Directory/consts';

function UploadCsv() {
  const analytics = useAnalytics();
  const classes = useStyles() as any;
  const sourceContext = useContext(SourceContext);
  const dispatch = useDispatch();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const { sourceInfo } = sourceContext;
  const { directoryId, sourceId, isPrimary } = sourceInfo;
  const getSource = makeGetSource(sourceId);
  const updateSourceFile = makeUpdateSourceFile(sourceId);
  const updateSourceFileStatus = useStatus(updateSourceFile);
  const getSourceFileValidationResults = makeGetSourceFileValidationResults(sourceId);
  const sourceFileValidationResults = useData(getSourceFileValidationResults);
  const sourceFileValidationResultsFetchStatus = useStatus(getSourceFileValidationResults);

  const csvSourceData = sourceData['csv'];
  const [file, setFile] = useState() as any;
  const [waiting, setWaiting] = useState(true);
  const [disableOptOut, setDisableOptOut] = useState(false);
  const [errorText, setErrorText] = useState<null | string>(null);
  const [csvIssuesDialogOpen, setCsvIssuesDialogOpen] = useState(false);
  const [fileUploadStatus, setFileUploadStatus] = useState<number>(LOADING);
  const [sourceFileId, setSourceFileId] = useState<string | null>(null);
  const status = combineStatuses(fileUploadStatus, sourceFileValidationResultsFetchStatus);
  const source = useSource(sourceId);
  const isOptedOutCsv: boolean = _get(sourceInfo, ['source', 'setupProgress', 'csvOptOut'], false);

  const disableNextButton = waiting || ((!file || !!errorText) && !isOptedOutCsv);
  const baseUrl = makeSourceCreateBaseUrl(directoryId, sourceId);

  useEffect(() => {
    if (isNormal(updateSourceFileStatus)) {
      setTimeout(() => {
        history.push(`${baseUrl}${MAP_ATTRIBUTES_URL}`);
      }, 1000);
    }
  }, [updateSourceFileStatus]);

  const handleFileChange = (file: any) => {
    if (!file) return;
    setFile(file);

    const fileTypeError = checkCsvUploadFileType(file);
    setErrorText(fileTypeError);

    if (fileTypeError) {
      analytics.track('Wizard - CSV Upload Error', {
        category: 'AD - Directories',
      });
    } else {
      analytics.track('Wizard - CSV Upload Success', {
        category: 'AD - Directories',
      });
    }

    setWaiting(false);
  };

  const handleNext = async () => {
    setCsvIssuesDialogOpen(true);
    setWaiting(true);
    setDisableOptOut(true);
    const sourceFile = await uploadCsvFile({
      file,
      sourceId,
      isActive: false,
      dispatch,
    });

    setFileUploadStatus(NORMAL);
    const { id: sourceFileId } = sourceFile;
    setSourceFileId(sourceFileId);
    await dispatch(getSourceFileValidationResults(sourceFileId, true));
  };

  const handleOptOutCsvUpload = async () => {
    setWaiting(true);
    setDisableOptOut(true);
    enqueueSnackbar('Saving your preferences...');

    const updatedSource = {
      ...source,
      setupProgress: {
        ..._get(sourceInfo, ['source', 'setupProgress'], {}),
        csvOptOut: true,
      },
    };
    await dispatch(saveSource(updatedSource));
    await dispatch(getSource());

    analytics.track('Wizard - Wizard - CSV Upload Skipped', {
      category: 'AD - Directories',
    });

    setTimeout(() => {
      history.push(`${baseUrl}${MAP_ATTRIBUTES_URL}`);
    }, 4000);
  };

  const handleContinue = async () => {
    analytics.track('Data Sync - Continue Button Clicked in Dialog', {
      category: 'AD - Directories',
    });

    if (isOptedOutCsv) {
      const updatedSource = {
        ...source,
        setupProgress: {
          ..._get(sourceInfo, ['source', 'setupProgress'], {}),
          csvOptOut: false,
        },
      };

      await dispatch(saveSource(updatedSource));
      await dispatch(dispatch(getSource()));
    }

    sourceFileId && (await dispatch(updateSourceFile({ isActive: true, sourceFileId })));
  };

  const handleSetFile = (file: any) => {
    setFile(file);
  };

  const handleNewUpload = () => {
    analytics.track('Data Sync - Upload New CSV Button Clicked in Dialog', {
      category: 'AD - Directories',
    });

    setCsvIssuesDialogOpen(false);
    setFile(undefined);
    setFileUploadStatus(LOADING);
  };

  return (
    <>
      <Base
        onNext={handleNext}
        currentStep={1}
        disableNextButton={disableNextButton}
        buttonText="Map your data"
        footer={
          <span>
            If you plan on using SFTP or don’t have your file ready, you can{' '}
            <Link
              onClick={!disableOptOut ? handleOptOutCsvUpload : () => {}}
              className={classNames(classes.link, { [classes.disabled]: disableOptOut })}
              color="secondary"
            >
              upload later
            </Link>
            .
          </span>
        }
      >
        <CardHeader title={'Upload a CSV'} subheader={csvSourceData.uploadCopy} />
        <div className={classes.fileDropContainer}>
          <FileUploadInfoCard isPrimary={isPrimary} />
          <FileDrop
            data-testid="csv-file-drop"
            hasError={!!errorText}
            onFileChange={handleFileChange}
            onSetFile={handleSetFile}
            file={file}
            errorText={errorText}
            aria-label="csv-file-drop"
          />
        </div>
      </Base>

      {!!file && (
        <CsvAnalysisDialog
          open={csvIssuesDialogOpen}
          sourceFileId={sourceFileId}
          sourceId={sourceId}
          status={status}
          validationResults={_get(sourceFileValidationResults, 'validationResults')}
          onNext={handleContinue}
          onNewUpload={handleNewUpload}
        />
      )}
    </>
  );
}

const useStyles = makeStyles((theme: any) => {
  return {
    fileDropContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
      marginBottom: theme.spacing(3),
    },
    fileDropRoot: {
      ...theme.typography.h6,
      padding: 0,
      justifyContent: 'center',
      height: 208,
    },
    link: {
      cursor: 'pointer',
    },
    disabled: {
      color: theme.palette.action.disabled,
      cursor: 'not-allowed',
    },
  };
});

export default UploadCsv;
